import {
  AgentComplianceReviewByShareLinkTokenQuery,
  PlatformRequirementIssueStatus,
} from '@client/graphql/__generated__/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import {
  CompliancePlatformRequirementIssue,
  ComplianceSuggestion,
} from '../components/types';
import { useAgentComplianceReviewDocument } from './useAgentComplianceReviewDocument';
import { useAgentComplianceReviewIgnoreAndResubmit } from './useAgentComplianceReviewIgnoreAndResubmit';
import { useAgentComplianceReviewIgnorePlatformRequirementIssue } from './useAgentComplianceReviewIgnorePlatformRequirementIssue';
import { useAgentComplianceReviewIgnoreSuggestion } from './useAgentComplianceReviewIgnoreSuggestion';
import { useAgentComplianceReviewKeyTerms } from './useAgentComplianceReviewKeyTerms';
import { useAgentComplianceReviewPlatformUrl } from './useAgentComplianceReviewPlatformUrl';
import { useAgentDocumentValidation } from './useAgentDocumentValidation';

type AgentComplianceReviewOverview =
  AgentComplianceReviewByShareLinkTokenQuery['agentComplianceReviewByShareLinkToken'];

interface UseAgentComplianceReviewArgs {
  overview: AgentComplianceReviewOverview;
  /** Currently selected document (from the `?documentId=` query param). */
  selectedDocumentId: string | undefined;
  /** Share-link token (`?token=` query param), preserved across navigation. */
  token: string | undefined;
  /** Folder UUID (route param) the review belongs to. */
  ingestionFolderUuid: string | undefined;
}

/**
 * Shared controller for the agent compliance review layouts. Owns the
 * per-document detail load, derived validation, the focused-field state, the
 * token-bound ignore / resubmit mutations, and the URL-driven document
 * selection — so the desktop three-pane layout and the mobile single-pane flow
 * render the same behavior from one source of truth.
 *
 * Selection is URL-driven: {@link onSelectDocument} navigates to
 * `compliance-review/:ingestionFolderUuid?token=…&documentId=…` (preserving the
 * token), and {@link onClearSelection} drops the `documentId` to return to the
 * overview (used by the mobile breadcrumb's back affordance).
 */
export const useAgentComplianceReview = ({
  overview,
  selectedDocumentId,
  token,
  ingestionFolderUuid,
}: UseAgentComplianceReviewArgs) => {
  const navigate = useNavigate();

  const { document, loading, error } = useAgentComplianceReviewDocument({
    token,
    ingestionFolderUuid,
    ingestionDocumentId: selectedDocumentId,
  });

  const { keyTerms } = useAgentComplianceReviewKeyTerms();

  // The platform deep-link ("Fix in SkySlope") is fetched ON DEMAND — only once
  // a document is open (the action buttons are shown). Resolving it calls the
  // SkySlope API, so keeping it off the overview means the page load and
  // post-mutation refetches never trigger that call. Skipped until a document
  // is selected.
  const { platformUrl } = useAgentComplianceReviewPlatformUrl({
    token,
    ingestionFolderUuid,
    skip: selectedDocumentId == null,
  });

  // The field id the user clicked a suggestion/check row to focus. Lifted here
  // so the suggestion/check lists and the DocumentViewer stay in sync: the
  // viewer scrolls/centers the PDF on it and gives it the active outline.
  const [activeFieldId, setActiveFieldId] = useState<string | undefined>(
    undefined
  );

  // Clear the focused field when the selected document changes — a field id
  // from the previous document is meaningless against the new one.
  useEffect(() => {
    setActiveFieldId(undefined);
  }, [selectedDocumentId]);

  const onSelectFields = useCallback((fieldIds: string[]) => {
    setActiveFieldId(fieldIds[0]);
  }, []);

  const onSelectDocument = useCallback(
    (ingestionDocumentId: string) => {
      const search = createSearchParams({
        ...(token != null ? { token } : {}),
        documentId: ingestionDocumentId,
      }).toString();
      navigate(`/compliance-review/${ingestionFolderUuid}?${search}`);
    },
    [navigate, token, ingestionFolderUuid]
  );

  // Drop the `?documentId=` selection (keeping the token) to return to the
  // overview. Backs the mobile breadcrumb's property/back affordance.
  const onClearSelection = useCallback(() => {
    const search =
      token != null ? `?${createSearchParams({ token }).toString()}` : '';
    navigate(`/compliance-review/${ingestionFolderUuid}${search}`);
  }, [navigate, token, ingestionFolderUuid]);

  // Derive suggestions / successful checks / field highlights on the client
  // from the raw document detail — reusing the same shared compliance utils as
  // the drawer/extension, so the transformation isn't forked onto the backend.
  const validation = useAgentDocumentValidation(document);

  // Token-bound mutations. The hooks bind the share-link token + folder uuid;
  // handlers only supply the suggestion-identifying / document fields. The
  // hooks await a refetch of the overview + open detail, so the backend-derived
  // counts/state drive the UI (no local optimistic guess).
  const [ignore, { loading: isIgnoring }] =
    useAgentComplianceReviewIgnoreSuggestion({ token, ingestionFolderUuid });
  const [ignorePlatformIssue, { loading: isIgnoringPlatformIssue }] =
    useAgentComplianceReviewIgnorePlatformRequirementIssue({
      token,
      ingestionFolderUuid,
    });
  const [ignoreAndResubmit, { loading: isSubmitting }] =
    useAgentComplianceReviewIgnoreAndResubmit({ token, ingestionFolderUuid });

  const ingestionDocumentValidationId =
    document?.ingestionDocumentValidationId ?? undefined;

  // Hide a single document-level suggestion. Keyed on the selected document +
  // its validation report + the suggestion's owning contract document / rule.
  const onIgnoreDocumentSuggestion = useCallback(
    (suggestion: ComplianceSuggestion) => {
      if (selectedDocumentId == null) {
        return;
      }
      void ignore({
        ingestionDocumentId: selectedDocumentId,
        ingestionDocumentValidationId,
        contractDocumentUuid: suggestion.contractDocumentUuid ?? undefined,
        ruleUuid: suggestion.ruleUuid ?? undefined,
        pageNumber: suggestion.pageNumbers?.[0],
      });
    },
    [ignore, selectedDocumentId, ingestionDocumentValidationId]
  );

  // Un-hide a previously AGENT-ignored document-level suggestion (Undo). Same
  // match keys as the ignore above, but `isHidden: false`.
  const onUndoIgnoreDocumentSuggestion = useCallback(
    (suggestion: ComplianceSuggestion) => {
      if (selectedDocumentId == null) {
        return;
      }
      void ignore({
        ingestionDocumentId: selectedDocumentId,
        ingestionDocumentValidationId,
        contractDocumentUuid: suggestion.contractDocumentUuid ?? undefined,
        ruleUuid: suggestion.ruleUuid ?? undefined,
        pageNumber: suggestion.pageNumbers?.[0],
        isHidden: false,
      });
    },
    [ignore, selectedDocumentId, ingestionDocumentValidationId]
  );

  // Hide a single folder-level suggestion. Keyed on the folder validation id +
  // the document discriminators carried on the overview suggestion.
  const onIgnoreFolderSuggestion = useCallback(
    (suggestion: ComplianceSuggestion) => {
      void ignore({
        ingestionFolderValidationId:
          suggestion.ingestionFolderValidationId ?? undefined,
        documentCategory: suggestion.documentCategory ?? undefined,
        documentForm: suggestion.documentForm ?? undefined,
        documentName: suggestion.documentName ?? undefined,
        ruleUuid: suggestion.ruleUuid ?? undefined,
      });
    },
    [ignore]
  );

  // Un-hide a previously ignored folder-level suggestion (Undo). Same match
  // keys as the folder ignore above, but `isHidden: false`.
  const onUndoIgnoreFolderSuggestion = useCallback(
    (suggestion: ComplianceSuggestion) => {
      void ignore({
        ingestionFolderValidationId:
          suggestion.ingestionFolderValidationId ?? undefined,
        documentCategory: suggestion.documentCategory ?? undefined,
        documentForm: suggestion.documentForm ?? undefined,
        documentName: suggestion.documentName ?? undefined,
        ruleUuid: suggestion.ruleUuid ?? undefined,
        isHidden: false,
      });
    },
    [ignore]
  );

  // Checklist mismatches ("platform requirement issues") come straight off the
  // document detail — the projection service already resolved their status /
  // actionability, so the read layer doesn't rederive any of it.
  const platformRequirementIssues: CompliancePlatformRequirementIssue[] =
    useMemo(
      () => document?.platformRequirementIssues ?? [],
      [document?.platformRequirementIssues]
    );

  // Hide a single checklist mismatch. Keyed on the selected document + the
  // projected public issue id (never the internal db id).
  const onIgnorePlatformRequirementIssue = useCallback(
    (issue: CompliancePlatformRequirementIssue) => {
      if (selectedDocumentId == null) {
        return;
      }
      void ignorePlatformIssue({
        ingestionDocumentId: selectedDocumentId,
        publicIssueId: issue.publicIssueId,
        isHidden: true,
      });
    },
    [ignorePlatformIssue, selectedDocumentId]
  );

  // Un-hide a previously AGENT-ignored checklist mismatch (Undo).
  const onUndoIgnorePlatformRequirementIssue = useCallback(
    (issue: CompliancePlatformRequirementIssue) => {
      if (selectedDocumentId == null) {
        return;
      }
      void ignorePlatformIssue({
        ingestionDocumentId: selectedDocumentId,
        publicIssueId: issue.publicIssueId,
        isHidden: false,
      });
    },
    [ignorePlatformIssue, selectedDocumentId]
  );

  // Open + actionable checklist mismatches are the ones a resubmit ignores.
  const openActionablePlatformRequirementIssues = useMemo(
    () =>
      platformRequirementIssues.filter(
        (issue) =>
          issue.status === PlatformRequirementIssueStatus.OPEN &&
          issue.isAgentActionable
      ),
    [platformRequirementIssues]
  );
  // For the resubmit note, list every actionable checklist mismatch the agent
  // could be declaring not relevant (open OR already agent-ignored).
  const resubmitNotePlatformRequirementIssues = useMemo(
    () =>
      platformRequirementIssues.filter(
        (issue) =>
          issue.isAgentActionable &&
          (issue.status === PlatformRequirementIssueStatus.OPEN ||
            issue.status === PlatformRequirementIssueStatus.IGNORED_BY_AGENT)
      ),
    [platformRequirementIssues]
  );

  // The resubmit modal's note is prepopulated as a friendly message to the
  // reviewer: a greeting, then ALL of the document's flagged issues — whether
  // the agent has crossed them out or not (USER-suppressed suggestions are
  // already filtered out upstream and never appear here). Resubmitting declares
  // them all not relevant, so they're framed that way. The backend appends the
  // "– {Agent} via Indigo" signature, so it's not included here.
  const allSuggestionMessages = [
    ...validation.suggestions.map((suggestion) => suggestion.message),
    ...resubmitNotePlatformRequirementIssues.map(
      (issue) => `Checklist suggestion: ${issue.message}`
    ),
  ];
  const defaultResubmitMessage = [
    "Hi, I've resubmitted this document for review.",
    ...(allSuggestionMessages.length > 0
      ? [
          `Here are the issues that I think are not relevant:\n${allSuggestionMessages
            .map((message) => `• ${message}`)
            .join('\n')}`,
        ]
      : []),
    'Thanks!',
  ].join('\n\n');

  // Resubmit the selected document with the agent's (possibly edited) note.
  // Clicking resubmit also ignores every still-open suggestion (the ones the
  // agent had NOT already crossed out) — already-ignored suggestions stay
  // hidden and are not re-sent. The backend hides them, posts the note to
  // SkySlope, and sets the item back to Pending. Once it lands, we auto-advance
  // to the next checklist item that still needs review so the agent can work
  // straight down the list.
  const onResubmit = useCallback(
    async (message: string) => {
      if (selectedDocumentId == null) {
        return;
      }
      const suggestionsToIgnore = validation.suggestions
        .filter((suggestion) => !suggestion.isIgnored)
        .map((suggestion) => ({
          contractDocumentUuid: suggestion.contractDocumentUuid ?? undefined,
          ruleUuid: suggestion.ruleUuid ?? undefined,
          pageNumber: suggestion.pageNumbers?.[0],
        }));
      // Validation suggestions need the current validation id to key their
      // review rows. Checklist-only resubmits do not, so let those through.
      if (
        suggestionsToIgnore.length > 0 &&
        ingestionDocumentValidationId == null
      ) {
        return;
      }
      const platformRequirementIssuePublicIdsToIgnore =
        openActionablePlatformRequirementIssues.map(
          (issue) => issue.publicIssueId
        );

      // Resolve the next item to advance to BEFORE the resubmit (the whole
      // checklist activity becomes resubmitted, so skip its documents): the
      // first remaining document — in a different activity — that needs review.
      const currentActivityId =
        overview.documents.find(
          (doc) => doc.ingestionDocumentId === selectedDocumentId
        )?.skyslopeActivityId ?? null;
      const nextDocument = overview.documents.find((doc) => {
        if (doc.ingestionDocumentId === selectedDocumentId) {
          return false;
        }
        if (
          currentActivityId != null &&
          doc.skyslopeActivityId === currentActivityId
        ) {
          return false;
        }

        return doc.needsReviewByAgent;
      });

      await ignoreAndResubmit({
        ingestionDocumentId: selectedDocumentId,
        ingestionDocumentValidationId,
        message,
        suggestionsToIgnore,
        platformRequirementIssuePublicIdsToIgnore,
      });

      if (nextDocument != null) {
        onSelectDocument(nextDocument.ingestionDocumentId);
      }
    },
    [
      ignoreAndResubmit,
      selectedDocumentId,
      ingestionDocumentValidationId,
      validation.suggestions,
      overview.documents,
      onSelectDocument,
      openActionablePlatformRequirementIssues,
    ]
  );

  const hasSelection = selectedDocumentId != null;

  // The selected document's summary row carries `agentResubmittedAt` — once set
  // the resubmit action is spent (disabled + relabeled) — and `needsReviewByAgent`,
  // which gates the document-level action buttons (only needs-review documents
  // can be resubmitted).
  const selectedDocSummary = overview.documents.find(
    (doc) => doc.ingestionDocumentId === selectedDocumentId
  );
  // `needsReviewByAgent` already excludes resubmitted documents, so a resubmitted
  // document reads as "no issues" (the resubmit action becomes the disabled
  // "Resubmitted").
  const selectedDocNeedsReview =
    selectedDocSummary?.needsReviewByAgent ?? false;

  return {
    document,
    loading,
    error,
    keyTerms,
    platformUrl,
    activeFieldId,
    validation,
    hasSelection,
    selectedDocSummary,
    selectedDocNeedsReview,
    platformRequirementIssues,
    isIgnoring,
    isIgnoringPlatformIssue,
    isSubmitting,
    defaultResubmitMessage,
    onSelectFields,
    onSelectDocument,
    onClearSelection,
    onIgnoreDocumentSuggestion,
    onUndoIgnoreDocumentSuggestion,
    onIgnoreFolderSuggestion,
    onUndoIgnoreFolderSuggestion,
    onIgnorePlatformRequirementIssue,
    onUndoIgnorePlatformRequirementIssue,
    onResubmit,
  };
};
